import { InfoForm } from 'components/DocumentsUploader/DocumentInfo/types';
import { Actor } from 'components/DocumentsUploader/types';
import { mapDocumentParametersToPayloadValue } from 'components/uploader/uploadUtils';
import { toDate } from 'lib/date';
import { sortByOrder } from 'lib/helpers';
import isEqual from 'lodash/isEqual';
import setUntyped from 'lodash/set';
import { DocumentFieldType } from 'services/documentFields/enums';
import {
    DocumentField,
    DocumentParameter,
} from 'services/documentFields/types';
import { UpdateDocumentPayload } from 'services/documents/ts/api';
import { Document } from 'services/documents/ts/types';
import logger from 'services/logger';

import {
    EditDocumentForm,
    EditDocumentFormWithoutMeta,
    RootFieldChangedMap,
} from './types';

import { useEditDocumentForm } from './useEditDocumentForm';

export const mapOwnerToCounterparty = (
    document: Document,
): EditDocumentFormWithoutMeta['counterparties'][number] => {
    return {
        edrpou: document.edrpouOwner!,
        name: document.companyNameOwner! || '',
        email: document.emailOwner || '', // emailOwner не може бути null [Nova Poshta]
        shouldSign: (document.expectedOwnerSignatures || 0) > 0,
        isEmailHidden: false,
    };
};

export const mapRecipientToCounterparty = (
    document: Document,
): EditDocumentFormWithoutMeta['counterparties'][number] => {
    return {
        edrpou: document.edrpouRecipient!,
        name: document.companyNameRecipient || '',
        email: document.emailRecipient || '', // emailOwner не може бути null [Nova Poshta]
        shouldSign: (document.expectedRecipientSignatures || 0) > 0,
        isEmailHidden: document.isRecipientEmailHidden!,
    };
};

export const mapFlowToCounterparty = (
    flow: Document['flows'][number],
): EditDocumentFormWithoutMeta['counterparties'][number] => {
    return {
        edrpou: flow.edrpou,
        name: flow.displayCompanyName || '',
        email: flow.receivers.emails[0] || '', // emailOwner не може бути null [Nova Poshta]
        shouldSign: flow.signaturesCount > 0,
        isEmailHidden: flow.receivers.is_emails_hidden,
    };
};

export const documentToCounterpartiesFields = (
    document: Document,
): Pick<EditDocumentFormWithoutMeta, 'counterparties' | 'isParallel'> => {
    if (document.isInternal) {
        return {
            isParallel: false,
            counterparties: [],
        };
    }

    if (document.isMultilateral) {
        const flows = document.flows || [];
        const isParallel = flows[0]?.order === null;

        return {
            isParallel,
            counterparties: isParallel
                ? // якщо порядок не вказаний, то відображаємо контрагентів які вже завершили підписання першими
                  [
                      ...flows.filter((item) => item.isComplete),
                      ...flows.filter((item) => !item.isComplete),
                  ].map(mapFlowToCounterparty)
                : // якщо порядок вказаний, то відображаємо контрагентів в порядку вказаному в документі
                  flows.toSorted(sortByOrder).map(mapFlowToCounterparty),
        };
    }

    if (document.firstSignBy === 'recipient') {
        return {
            isParallel: false,
            counterparties: [
                mapRecipientToCounterparty(document),
                mapOwnerToCounterparty(document),
            ],
        };
    }

    if (document.firstSignBy === 'owner' && document.edrpouRecipient) {
        return {
            isParallel: false,
            counterparties: [
                mapOwnerToCounterparty(document),
                mapRecipientToCounterparty(document),
            ],
        };
    }

    return {
        isParallel: false,
        counterparties: [],
    };
};

export const documentToInfoFields = (
    document: Document,
): Pick<
    EditDocumentFormWithoutMeta,
    'amount' | 'title' | 'number' | 'date' | 'category'
> => {
    return {
        amount:
            document.amount || document.amount == 0 ? document.amount : null,
        title: document.title || '',
        number: document.number || '',
        category:
            typeof document.category === 'number'
                ? String(document.category)
                : null,
        date: document.dateDocument ? new Date(document.dateDocument) : null,
    };
};

const reviewRequestToActor = (
    reviewer: Document['reviewRequests'][number],
): Actor => {
    return {
        id: reviewer.toRoleId || reviewer.toGroupId,
        type: reviewer.toRoleId ? 'role' : 'group',
        // потрібно прокидати id, оскільки відсутня можливість отримати id з об'єкта
        source: reviewer.toRoleId
            ? { ...reviewer.toRole, id: reviewer.toRoleId }
            : { ...reviewer.toGroup, id: reviewer.toGroupId },
    } as Actor;
};

const reviewToActor = (reviewer: Document['reviews'][number]): Actor => {
    return {
        id: reviewer.groupId || reviewer.roleId, // використовуємо groupId, оскільки він в погодженні або присутній або відсутній, roleId завжди присутній і по ньому неможливо визначити чи це група чи роль
        type: reviewer.groupId ? 'group' : 'role',
        // потрібно прокидати id, оскільки відсутня можливість отримати id з об'єкта
        source: reviewer.groupId
            ? { ...reviewer.group, id: reviewer.groupId }
            : { ...reviewer.role, id: reviewer.roleId },
    } as Actor;
};

export const documentToEmployeeReviewersFields = (
    document: Document,
): Pick<
    EditDocumentFormWithoutMeta,
    | 'employeesReviewers'
    | 'employeesReviewersIsOrdered'
    | 'employeesIsSignAfterReview'
    | 'employeesWhichReviewed'
> => {
    const employeesWhichReviewed =
        document.reviews
            ?.filter((item) => item.type !== null) // null - означає було скасовано вибір
            ?.map(reviewToActor) ?? [];
    const reviewedActorsId = employeesWhichReviewed.map((actor) => actor.id);

    return {
        employeesWhichReviewed,
        employeesReviewers:
            document.reviewRequests
                ?.toSorted(sortByOrder)
                .map(reviewRequestToActor)
                .filter((item) => !reviewedActorsId.includes(item.id)) ?? [],
        employeesReviewersIsOrdered: !(
            document.reviewSetting?.isParallel ?? true
        ),
        employeesIsSignAfterReview: document.reviewSetting?.isRequired ?? false,
    };
};

export const signerToActor = (signer: Document['signers'][number]): Actor => {
    return {
        id: signer.roleId || signer.groupId,
        type: signer.roleId ? 'role' : 'group',
        // потрібно прокидати id, оскільки відсутня можливість отримати id з об'єкта
        source: signer.roleId
            ? { ...signer.role, id: signer.roleId }
            : { ...signer.group, id: signer.groupId },
    } as Actor;
};

export const sortSigners = (
    a: Document['signers'][number],
    b: Document['signers'][number],
): number => {
    if (typeof a.order === 'number' && typeof b.order === 'number') {
        return a.order - b.order;
    }

    return 0;
};

export const getSignedActors = (doc: Document): Actor[] => {
    return doc.signers
        .toSorted(sortByOrder)
        .filter((signer) =>
            doc.signatures.some(
                (signature) =>
                    signature.roleId === signer.roleId ||
                    signer.group.members.some(
                        (member) => member.role.id === signature.roleId,
                    ),
            ),
        )
        .map(signerToActor);
};

export const documentToEmployeesSignersFields = (
    document: Document,
): Pick<
    EditDocumentFormWithoutMeta,
    'employeesSigners' | 'employeesSignIsOrdered' | 'employeesWhichSigned'
> => {
    const employeesWhichSigned = getSignedActors(document);
    const signedActorsId = employeesWhichSigned.map((actor) => actor.id);

    return {
        employeesWhichSigned,
        employeesSigners:
            document.signers
                ?.toSorted(sortSigners)
                .map(signerToActor)
                .filter((item) => !signedActorsId.includes(item.id)) ?? [],
        employeesSignIsOrdered:
            typeof document.signers?.[0]?.order === 'number',
    };
};

export const documentToEmployeeViewersFields = (
    document: Document,
): Pick<EditDocumentFormWithoutMeta, 'employeesViewers'> => {
    return {
        employeesViewers: document.accesses.map(
            (viewAccess) =>
                ({
                    id: viewAccess.roleId,
                    type: 'role',
                    source: {
                        id: viewAccess.roleId,
                        user: viewAccess.role.user,
                        isAdmin: false, // підставляємо значення типово, оскільки це не використовується
                        canSignAndRejectDocument: true, // підставляємо значення типово, оскільки це не використовується
                    },
                } as Actor),
        ),
    };
};

const mapDocTagsToFormSelectedTags = (
    tags: Document['tags'],
): EditDocumentFormWithoutMeta['selectedTags'] =>
    tags.map((tag) => ({
        ...tag,
        // додаємо isNew, щоб відстежувати нові теги у формі
        isNew: false,
    }));

export const documentToFormFields = (
    document: Document,
): EditDocumentFormWithoutMeta => {
    return {
        dataAI: null,
        isInternal: document.isInternal,
        selectedTags: mapDocTagsToFormSelectedTags(document.tags),
        isVersioned: Boolean(document.versions?.length),
        templateId: null, // це значення завжди по замовчуванню null
        documentParameters: [], // дані додаємо ассинхронно виконуючи при необхідності запит до бекенду
        ...documentToInfoFields(document),
        ...documentToCounterpartiesFields(document),
        ...documentToEmployeeViewersFields(document),
        ...documentToEmployeeReviewersFields(document),
        ...documentToEmployeesSignersFields(document),
    };
};

export const normalizeAmount = (
    amount: Nullable<string | number>,
): Nullable<string> => {
    if (amount === null || amount === undefined) {
        return null;
    }

    if (amount === '') {
        return null;
    }

    if (typeof amount === 'string') {
        return String(parseFloat((parseFloat(amount) * 100).toFixed(2)));
    }

    return String(parseFloat((parseFloat(String(amount)) * 100).toFixed(2)));
};

export const normalizeDate = (date: Nullable<Date>): Nullable<Date> => {
    if (!date) {
        return null;
    }

    return toDate(date);
};

export const editFormToEmployeeSignersPayload = (
    form: Pick<
        EditDocumentFormWithoutMeta,
        'employeesSigners' | 'employeesSignIsOrdered'
    > &
        Partial<Pick<EditDocumentFormWithoutMeta, 'employeesWhichSigned'>>,
): Pick<UpdateDocumentPayload, 'signers_settings'> => {
    return {
        signers_settings: {
            parallel_signing: !form.employeesSignIsOrdered,
            entities: [
                ...(form.employeesWhichSigned || []),
                ...form.employeesSigners,
            ].map((signer) => ({
                id: signer.id,
                type: signer.type,
            })),
        },
    };
};

export const editFormToEmployeeViewersPayload = (
    form: Pick<EditDocumentFormWithoutMeta, 'employeesViewers'>,
): Pick<UpdateDocumentPayload, 'viewers_settings'> => {
    return {
        viewers_settings: {
            replace_viewers: form.employeesViewers.map((viewer) => viewer.id),
        },
    };
};

export const editFormToEmployeeReviewersPayload = (
    form: Pick<
        EditDocumentFormWithoutMeta,
        | 'employeesReviewers'
        | 'employeesIsSignAfterReview'
        | 'employeesReviewersIsOrdered'
    > &
        Partial<Pick<EditDocumentFormWithoutMeta, 'employeesWhichReviewed'>>,
): Pick<UpdateDocumentPayload, 'reviews_settings'> => {
    return {
        reviews_settings: {
            reviewers: [
                ...(form.employeesWhichReviewed || []),
                ...form.employeesReviewers,
            ].map((reviewer) => ({
                id: reviewer.id,
                type: reviewer.type,
            })),
            is_required: form.employeesIsSignAfterReview,
            is_ordered: form.employeesReviewersIsOrdered,
        },
    };
};

export const editFormToCounterpartiesPayload = (
    form: Pick<
        EditDocumentFormWithoutMeta,
        'counterparties' | 'isParallel' | 'isInternal'
    >,
): Pick<UpdateDocumentPayload, 'recipients_settings'> => {
    if (form.isInternal) {
        return {
            recipients_settings: {
                is_internal: true,
            },
        };
    }

    return {
        recipients_settings: {
            is_internal: false,
            recipients: form.counterparties.map((counterparty) => ({
                edrpou: counterparty.edrpou,
                is_email_hidden: counterparty.isEmailHidden,
                role: counterparty.shouldSign ? 'signer' : 'viewer',
                emails: counterparty.isEmailHidden
                    ? null
                    : [counterparty.email],
            })),
            is_ordered: form.counterparties.length <= 2 || !form.isParallel,
        },
    };
};

export const editFormToVersionSettingsPayload = (
    form: Pick<EditDocumentFormWithoutMeta, 'isVersioned'>,
): Pick<UpdateDocumentPayload, 'version_settings'> => {
    return {
        version_settings: {
            is_versioned: form.isVersioned,
        },
    };
};

export const editFormToTagSettingsPayload = (
    form: Pick<EditDocumentFormWithoutMeta, 'selectedTags'>,
): Pick<UpdateDocumentPayload, 'tags_settings'> => {
    return {
        tags_settings: {
            replace: {
                tags_ids: form.selectedTags
                    .filter((tag) => !tag.isNew)
                    .map((tag) => tag.id),
                new_tags: form.selectedTags
                    .filter((tag) => tag.isNew)
                    .map((tag) => tag.name),
            },
        },
    };
};

interface EditFormToPayload {
    form: EditDocumentForm;
    doc: Document;
    init: ReturnType<typeof useEditDocumentForm>['formState']['defaultValues'];
}

const getFieldsChanged = (
    init: EditFormToPayload['init'],
    form: EditFormToPayload['form'],
): RootFieldChangedMap => {
    return Object.keys(form).reduce((acc, key: keyof RootFieldChangedMap) => {
        if (key === 'amount') {
            acc[key] = init
                ? !isEqual(
                      normalizeAmount(
                          init.amount as EditDocumentForm['amount'],
                      ),
                      normalizeAmount(form.amount),
                  )
                : true;
            return acc;
        }

        if (key === 'category') {
            acc[key] = String(init?.category) !== String(form.category);
            return acc;
        }

        if (key === 'date') {
            acc[key] = init
                ? !isEqual(
                      normalizeDate(init.date as EditDocumentForm['date']),
                      normalizeDate(form.date),
                  )
                : true;
            return acc;
        }

        // якщо не передати init, то всі поля будуть вважатись зміненими
        acc[key] = init ? !isEqual(init[key], form[key]) : true;
        return acc;
    }, {} as RootFieldChangedMap);
};

type DocSettingInfoKey = `document_settings.${keyof InfoForm}`;

export const editFormToPayload = ({
    form,
    init,
    doc,
}: EditFormToPayload): UpdateDocumentPayload => {
    const payload: UpdateDocumentPayload = {};
    const updated = getFieldsChanged(init, form);
    const isOwner = doc.edrpouOwner === form.companyEdrpou;

    if (updated.employeesSignIsOrdered || updated.employeesSigners) {
        Object.assign(payload, editFormToEmployeeSignersPayload(form));
    }

    if (updated.employeesViewers) {
        Object.assign(payload, editFormToEmployeeViewersPayload(form));
    }

    if (
        updated.employeesReviewers ||
        updated.employeesIsSignAfterReview ||
        updated.employeesReviewersIsOrdered
    ) {
        Object.assign(payload, editFormToEmployeeReviewersPayload(form));
    }

    if (isOwner && updated.number) {
        setUntyped(
            payload,
            'document_settings.number' as DocSettingInfoKey,
            form.number,
        );
    }

    if (isOwner && updated.title) {
        setUntyped(
            payload,
            'document_settings.title' as DocSettingInfoKey,
            form.title,
        );
    }

    if (isOwner && updated.date) {
        setUntyped(
            payload,
            'document_settings.date_document' as DocSettingInfoKey,
            normalizeDate(form.date),
        );
    }

    if (isOwner && updated.category) {
        setUntyped(
            payload,
            'document_settings.category' as DocSettingInfoKey,
            form.category,
        );
    }

    if (isOwner && updated.amount) {
        setUntyped(
            payload,
            'document_settings.amount' as DocSettingInfoKey,
            normalizeAmount(form.amount),
        );
    }

    if (updated.selectedTags) {
        Object.assign(payload, editFormToTagSettingsPayload(form));
    }

    if (
        isOwner &&
        (updated.counterparties || updated.isInternal || updated.isParallel)
    ) {
        Object.assign(payload, editFormToCounterpartiesPayload(form));
    }

    if (isOwner && updated.isVersioned) {
        Object.assign(payload, editFormToVersionSettingsPayload(form));
    }

    if (updated.documentParameters) {
        const docParametersAfterSave = new Set(
            form.documentParameters.map((param) => param.id),
        );

        payload.parameters_settings = {
            update_parameters: form.documentParameters.map((param) => ({
                value: mapDocumentParametersToPayloadValue(param),
                field_id: param.id,
                is_required: param.isRequired,
            })),
            delete_parameters:
                doc.parameters
                    ?.map((param) => param.fieldId)
                    .filter((id) => !docParametersAfterSave.has(id)) ?? [],
        };
    }

    if (form.templateId) {
        payload.template_id = form.templateId;
    }

    return payload;
};

export const docParametersToFormParameters = (
    documentParameters: DocumentParameter[],
    documentFields: DocumentField[],
): EditDocumentForm['documentParameters'] => {
    return documentParameters
        .map((parameter) => {
            const documentField = documentFields.find(
                (field) => field.id === parameter.fieldId,
            );

            if (!documentField) {
                logger.error(
                    '[edit document] Failed to apply additional filed to from data',
                    parameter,
                );
                return null;
            }

            let value: EditDocumentForm['documentParameters'][number]['value'] = parameter.value as string;

            if (documentField.type === DocumentFieldType.date && value) {
                value = new Date(value);
            }

            if (
                [DocumentFieldType.number, DocumentFieldType.text].includes(
                    documentField.type,
                )
            ) {
                value = value === null ? '' : value;
            }

            return {
                ...documentField,
                isRequired: parameter.isRequired, // відображаємо обов'язковість поля
                value,
            } as EditDocumentForm['documentParameters'][number];
        })
        .filter(Boolean) as EditDocumentForm['documentParameters'];
};
